import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	InputAdornment,
	IconButton,
	Menu,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Chip,
	Tooltip,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { FornecedorService } from "../../service/fornecedor_service";

function Fornecedores() {
	const navigate = useNavigate();
	const [fornecedores, setFornecedores] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [total, setTotal] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [solicitacoesPendentes, setSolicitacoesPendentes] = useState(0);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	useEffect(() => {
		if (!unidadeDistribuidoraId) return;
		const service = new FornecedorService();
		service.getSolicitacoes(unidadeDistribuidoraId, "PENDENTE")
			.then((data) => setSolicitacoesPendentes(data?.length || 0))
			.catch(() => {});
	}, [unidadeDistribuidoraId]);

	const loadFornecedores = useCallback(async () => {
		setLoading(true);
		try {
			const fornecedorService = new FornecedorService();
			const result = await fornecedorService.getAll(unidadeDistribuidoraId, page + 1, rowsPerPage, searchTerm);
			setFornecedores(result.data || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Erro ao carregar fornecedores:", error);
			alert(error.message || "Erro ao carregar fornecedores");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, page, rowsPerPage, searchTerm]);

	useEffect(() => {
		if (unidadeDistribuidoraId) {
			loadFornecedores();
		}
	}, [unidadeDistribuidoraId, loadFornecedores]);

	// Debounced search effect
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setPage(0);
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [searchTerm]);

	const handleNovaCadastro = () => {
		navigate("/fornecedores/cadastro");
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleOpenMenu = (event, item) => {
		setAnchorEl(event.currentTarget);
		setSelectedItem(item);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedItem(null);
	};

	const handleEdit = () => {
		if (selectedItem) {
			navigate(`/fornecedores/cadastro/${selectedItem.id}`);
		}
		handleCloseMenu();
	};

	const handleDeleteClick = () => {
		setItemToDelete(selectedItem);
		setOpenDialog(true);
		handleCloseMenu();
	};

	const handleConfirmDelete = async () => {
		if (itemToDelete) {
			try {
				const fornecedorService = new FornecedorService();
				await fornecedorService.changeVisibility(itemToDelete.id);
				setOpenDialog(false);
				setItemToDelete(null);
				loadFornecedores();
			} catch (error) {
				console.error("Erro ao excluir fornecedor:", error);
				alert(error.message || "Erro ao excluir fornecedor");
			}
		}
	};

	const handleCancelDelete = () => {
		setOpenDialog(false);
		setItemToDelete(null);
	};

	const formatCpfCnpj = (value) => {
		if (!value) return "-";
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length <= 11) {
			return cleanValue
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		} else {
			return cleanValue
				.replace(/(\d{2})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1/$2")
				.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
		}
	};

	const formatTelefone = (value) => {
		if (!value) return "-";
		let digits = value.replace(/\D/g, "");
		if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) digits = digits.slice(2);
		if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
		if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
		return value;
	};

	// Mesma regra da tela de WhatsApp: sem telefone o backend descarta a mensagem
	// e fornecedor oculto nao pode ser selecionado para envio.
	const motivoSemWhatsApp = (forn) => {
		if (forn.oculto) return "Fornecedor inativo";
		if (!forn.telefone?.replace(/\D/g, "")) return "Sem telefone";
		return null;
	};

	return (
		<AppLayout currentPage="fornecedores">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com busca */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
						}}
					>
						<Typography
							variant="h4"
							sx={{
								color: "#0f4557",
								fontWeight: 700,
								fontFamily: "'Plus Jakarta Sans', sans-serif",
							}}
						>
							Fornecedores
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<TextField
								size="small"
								placeholder="Buscar..."
								value={searchTerm}
								onChange={handleSearchChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{
									width: "300px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleNovaCadastro}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
										transform: "translateY(-1px)",
										boxShadow: "0 4px 12px rgba(26, 158, 143, 0.3)",
									},
									textTransform: "none",
									px: 3,
									py: 1.2,
									borderRadius: "14px",
									fontWeight: 600,
								}}
							>
								Novo Fornecedor
							</Button>
							{solicitacoesPendentes > 0 && (
								<Button
									variant="outlined"
									startIcon={<NotificationsActiveIcon />}
									onClick={() => navigate("/solicitacoes")}
									sx={{
										borderColor: "#f57c00",
										color: "#f57c00",
										"&:hover": { borderColor: "#e65100", color: "#e65100" },
										textTransform: "none",
										px: 3,
										py: 1.2,
										borderRadius: "14px",
										fontWeight: 600,
									}}
								>
									Solicitações Pendentes ({solicitacoesPendentes})
								</Button>
							)}
						</Box>
					</Box>

					{/* Tabela */}
					<Paper
						sx={{
							bgcolor: "white",
							borderRadius: "16px",
							boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
							border: "1px solid #dfe3e8",
							overflow: "hidden",
						}}
					>
						{loading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "400px",
								}}
							>
								<CircularProgress sx={{ color: "#1a9e8f" }} />
							</Box>
						) : fornecedores.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "400px",
									gap: 2,
									p: 4,
								}}
							>
								<Typography
									variant="h6"
									sx={{ color: "#5a7a88", textAlign: "center" }}
								>
									Nenhum fornecedor encontrado
								</Typography>
								<Typography
									variant="body2"
									sx={{ color: "#99b5b0", textAlign: "center" }}
								>
									{searchTerm
										? "Tente fazer uma busca diferente"
										: 'Clique no botão "Novo Fornecedor" para começar'}
								</Typography>
							</Box>
						) : (
							<>
								<TableContainer>
									<Table>
										<TableHead>
											<TableRow sx={{ bgcolor: "#f0f5f4" }}>
												<TableCell sx={{ fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
													Nome
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
													CPF/CNPJ
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
													Telefone
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
													WhatsApp
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
													Email
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
													Cidade/UF
												</TableCell>
												<TableCell
													sx={{ fontWeight: 600, color: "#0f4557", width: "80px", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
													align="center"
												>
													Ações
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{fornecedores.map((forn) => (
												<TableRow
													key={forn.id}
													sx={{
														"&:hover": {
															bgcolor: "#f8fafa",
														},
														transition: "background-color 0.2s ease",
													}}
												>
													<TableCell>
														<Typography variant="body2" sx={{ fontWeight: 500 }}>
															{forn.nome}
														</Typography>
														{forn.nomeFantasia && (
															<Typography
																variant="caption"
																sx={{ color: "#5a7a88", display: "block" }}
															>
																{forn.nomeFantasia}
															</Typography>
														)}
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{formatCpfCnpj(forn.cpfCnpj)}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
															{formatTelefone(forn.telefone)}
														</Typography>
													</TableCell>
													<TableCell>
														{motivoSemWhatsApp(forn) ? (
															<Tooltip title="Não recebe as mensagens de coleta pelo WhatsApp">
																<Chip
																	size="small"
																	label={motivoSemWhatsApp(forn)}
																	sx={{ bgcolor: "#fdecec", color: "#c62828", fontWeight: 500 }}
																/>
															</Tooltip>
														) : (
															<Tooltip title="Pode receber as mensagens de coleta pelo WhatsApp">
																<Chip
																	size="small"
																	icon={<WhatsAppIcon sx={{ fontSize: 16, color: "#1e8e4e !important" }} />}
																	label="Pode receber"
																	sx={{ bgcolor: "#e6f6ec", color: "#1e8e4e", fontWeight: 500 }}
																/>
															</Tooltip>
														)}
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{forn.email || "-"}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{forn.endereco?.cidade && forn.endereco?.uf
																? `${forn.endereco.cidade} - ${forn.endereco.uf}`
																: "-"}
														</Typography>
													</TableCell>
													<TableCell align="center">
														<IconButton
															size="small"
															onClick={(e) => handleOpenMenu(e, forn)}
															sx={{ color: "#5a7a88", "&:hover": { bgcolor: "rgba(26, 158, 143, 0.08)" } }}
														>
															<MoreVertIcon />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									component="div"
									count={total}
									page={page}
									onPageChange={handleChangePage}
									rowsPerPage={rowsPerPage}
									onRowsPerPageChange={handleChangeRowsPerPage}
									rowsPerPageOptions={[5, 10, 25, 50]}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) =>
										`${from}-${to} de ${count}`
									}
									sx={{
										borderTop: "1px solid #e0e0e0",
										".MuiTablePagination-select": {
											borderRadius: "4px",
										},
									}}
								/>
							</>
						)}
					</Paper>
			</Box>

			{/* Menu de ações */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleEdit} sx={{ borderRadius: "8px", mx: 1 }}>
					<EditIcon fontSize="small" sx={{ mr: 1, color: "#1a9e8f" }} />
					Editar
				</MenuItem>
				<MenuItem onClick={handleDeleteClick} sx={{ borderRadius: "8px", mx: 1 }}>
					<DeleteIcon fontSize="small" sx={{ mr: 1, color: "#ff4d4d" }} />
					Excluir
				</MenuItem>
			</Menu>

			{/* Dialog de confirmação */}
			<Dialog open={openDialog} onClose={handleCancelDelete}>
				<DialogTitle>Confirmar Exclusão</DialogTitle>
				<DialogContent>
					<Typography sx={{ color: "#0f4557" }}>
						Tem certeza que deseja excluir o fornecedor "
						{itemToDelete?.nome}"?
					</Typography>
					<Typography variant="caption" sx={{ color: "#5a7a88", mt: 1, display: "block" }}>
						Esta ação não poderá ser desfeita.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={handleCancelDelete} sx={{ color: "#5a7a88", borderRadius: "10px" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirmDelete}
						variant="contained"
						sx={{
							bgcolor: "#ff4d4d",
							borderRadius: "10px",
							"&:hover": {
								bgcolor: "#e64545",
							},
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>
		</AppLayout>
	);
}

export default Fornecedores;
